import { Brand, Category, Crop } from '@dehaat/kisan-app-bl/models/Product';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import CrossIcon from '@/components/icons/Cross';
import FilterIcon from '@/components/icons/Filter';
import { FilterTypes } from '@/constants/filterTypes';
import { FilterObjProps, ProductsQueryType } from '@/models/Product';
import scrollStyles from '@/styles/HideScrollbar.module.css';
import styles from '@/styles/ProductResults.module.css';
import { getBrandsFilterList, getCategoryFilterList, getCropsFilterList } from '@/utils/helpers/getFilterParams';
import Paragraph, { VARIANT } from '../Paragraph';
interface Props {
  appliedCategoryIds: string[];
  appliedBrandIds: string[];
  appliedCropIds: string[];
  brands?: Brand[];
  crops?: Crop[];
  categories?: Category[];
  defaultHighlighted?: FilterTypes;
  showOnlyFilterModal?: boolean;
  closeModal?: VoidFunction;
  pathname?: string;
  filterTypesToHide?: FilterTypes[];
  productCount?: number;
  className?: string;
}
const FiltersModal = dynamic(() => import('./FiltersModal'), {
  ssr: false,
  loading: () => null
});
const ProductFilter = ({
  appliedCategoryIds,
  appliedBrandIds,
  appliedCropIds,
  brands = [],
  categories = [],
  crops = [],
  defaultHighlighted,
  showOnlyFilterModal = false,
  closeModal,
  pathname,
  filterTypesToHide,
  productCount = 0,
  className = ''
}: Props) => {
  const {
    t
  } = useTranslation('products');
  const {
    replace,
    query
  } = useRouter();
  const [tempFilters, setTempFilters] = useState<FilterObjProps[]>([]);
  const [isFiltersModalOpen, setIsFiltersModalOpen] = useState(false);
  const appliedBrands = useMemo(() => brands ? getBrandsFilterList(brands.filter(brand => appliedBrandIds.includes(brand.id))) : [], [brands, appliedBrandIds]);
  const appliedCategories = useMemo(() => categories ? getCategoryFilterList(categories.filter(category => appliedCategoryIds.includes(category.id))) : [], [categories, appliedCategoryIds]);
  const appliedCrops = useMemo(() => crops ? getCropsFilterList(crops.filter(crop => appliedCropIds.includes(crop.id))) : [], [crops, appliedCropIds]);
  const combinedAppliedFilters = useMemo(() => [...appliedCategories, ...appliedBrands, ...appliedCrops], [appliedCategories, appliedBrands, appliedCrops]);
  const [appliedFilters, setAppliedFilters] = useState<FilterObjProps[]>(combinedAppliedFilters);
  const onBubbleRemove = useCallback((bubble: FilterObjProps) => {
    const newBrandIds = appliedBrandIds.filter(id => bubble.type !== FilterTypes.BRANDS || bubble.type === FilterTypes.BRANDS && id !== bubble.id);
    const newCategoryIds = appliedCategoryIds.filter(id => bubble.type !== FilterTypes.CATEGORY || bubble.type === FilterTypes.CATEGORY && id !== bubble.id);
    const newCropIds = appliedCropIds.filter(id => bubble.type !== FilterTypes.CROPS || bubble.type === FilterTypes.CROPS && id !== bubble.id);
    const newQuery: Record<string, string> = {};
    const subTag = query.sub_tag_id;
    if (typeof subTag === 'string') {
      newQuery.sub_tag_id = subTag;
    }
    if (newBrandIds.length > 0) {
      newQuery.b = newBrandIds.join('-');
    }
    if (newCategoryIds.length > 0) {
      newQuery.c = newCategoryIds.join('-');
    }
    if (newCropIds.length > 0) {
      newQuery.cr = newCropIds.join('-');
    }
    const newPathname = pathname?.split('?')[0];
    replace({
      pathname: newPathname,
      query: newQuery
    }, undefined, {
      shallow: true
    });
  }, [appliedBrandIds, appliedCategoryIds, appliedCropIds, pathname, replace]);
  const onTempFilterToggle = useCallback((isChecked: boolean, toggledFilter: FilterObjProps) => {
    setTempFilters(prev => {
      if (isChecked) {
        return prev.concat(toggledFilter);
      }
      return prev.filter(filter => filter.type !== toggledFilter.type || filter.type === toggledFilter.type && filter.id !== toggledFilter.id);
    });
  }, []);
  const handleOpenModal = useCallback(() => {
    setTempFilters([...appliedFilters]);
    setIsFiltersModalOpen(true);
  }, [appliedFilters]);
  const handleCloseModal = useCallback(() => {
    setTempFilters([...appliedFilters]);
    setIsFiltersModalOpen(false);
    closeModal?.();
  }, [appliedFilters, closeModal]);
  const filterBubbles = useMemo(() => appliedFilters.map(bubble => <div key={bubble.key} className="flex items-center bg-green-50 mr-2 p-2 border border-primary-100 border-solid rounded-3xl max-w-fit">
          <Paragraph className="whitespace-nowrap" variant={VARIANT.MEDIUM}>
            {bubble.name}
          </Paragraph>
          <button className="ml-1" onClick={() => onBubbleRemove(bubble)} data-testid={`remove-product-filter-${bubble.key}`}>
            <CrossIcon className="fill-transparent stroke-neutral-80 w-4 h-4" />
          </button>
        </div>), [appliedFilters, onBubbleRemove]);
  useEffect(() => {
    const newAppliedFilters = [...appliedCategories, ...appliedBrands, ...appliedCrops];
    setAppliedFilters(newAppliedFilters);
    setTempFilters(newAppliedFilters);
  }, [appliedCategoryIds, appliedBrandIds, appliedCropIds]);
  useEffect(() => {
    if (showOnlyFilterModal) {
      handleOpenModal();
    }
  }, [showOnlyFilterModal]);
  return <>
      {!showOnlyFilterModal && <div className={`bg-gray-10 border-b border-gray-20 border-t flex flex-col items-center px-4 md:px-8 py-2 w-full z-20 sticky top-0 h-fit ${className}`}>
          <div className="flex justify-between items-center w-full">
            {' '}
            <h2 className="font-semibold text-xl text-start">
              {t('all_products')} {productCount ? `(${productCount})` : ''}
            </h2>
            <div className="mx-2 h-8"></div>
            <button onClick={handleOpenModal} className="relative flex items-center bg-white px-2 py-1 border-[1px] border-gray-20 rounded-lg" data-testid="product-filters-toggle-btn">
              <FilterIcon className="w-6 h-6" fill={'#0F8040'} />
              <p className="mx-2 font-semibold text-primary-100 text-sm">
                {t('filter')}
              </p>
            </button>
          </div>
          {appliedFilters?.length > 0 ? <div className={`flex w-full flex-grow overflow-x-scroll mt-2 ${styles.bubblesContainer} ${scrollStyles.scroll}`}>
              {filterBubbles}
            </div> : null}
        </div>}
      {isFiltersModalOpen && <FiltersModal newFilters={tempFilters} onFilterToggle={onTempFilterToggle} brands={brands} categories={categories} crops={crops} onModalClose={handleCloseModal} defaultHighlighted={defaultHighlighted} pathname={pathname} filterTypesToHide={filterTypesToHide} />}
    </>;
};
export default memo(ProductFilter);